import { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { HelpCircle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

// Local / Invitational competitions support a secondary schedule
// regardless of whether the organiser has nominated a sub-competition
// name, and the secondary schedule is presented as a
// "Supplementary Schedule" (the SkateNZ rules around a recognised
// sub-competition don't apply at the Local / Invitational tier).
const SUPPLEMENTARY_TYPES = new Set(['Local', 'Invitational']);

export function EventConfigSection({ formData, setFormData, schedules, readOnly }) {
  const [helpOpen, setHelpOpen] = useState(false);

  const isSupplementaryTier = SUPPLEMENTARY_TYPES.has(formData.competition_type);
  const showSecondSchedule = isSupplementaryTier || !!formData.sub_competition_name;
  const secondScheduleLabel = isSupplementaryTier
    ? 'Supplementary Schedule'
    : 'Sub Competition Schedule';
  const helpTitle = isSupplementaryTier
    ? 'What is a Supplementary Schedule?'
    : 'What is a Sub Competition Schedule?';
  const helpBody = isSupplementaryTier
    ? 'If you\'re running a local or invitational competition with a standard schedule of events but you would like to offer additional events a supplementary schedule allows you to do that. For example, Pacific Cup offered WorldSkate events but also the Oceania Masters Schedule - that\'s a situation where this would be used.'
    : 'Events like a Regional or Area Championship are recognised SkateNZ events and follow the SkateNZ competitive schedule. To offer other events such as beginner grades a secondary competition is required - this is your sub competition.';

  const getAvailableSubSchedules = () => {
    if (!formData.schedule_id) return schedules;
    return schedules.filter(s => s.schedule_id !== formData.schedule_id);
  };

  return (
    <div className="p-4 border rounded-lg bg-green-50">
      <h4 className="font-medium mb-4 text-green-800">Event Configuration</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="flex items-center gap-2 h-6">
            <Label>Schedule</Label>
          </div>
          <select
            className="w-full border rounded-lg px-3 py-2 mt-1"
            value={formData.schedule_id}
            onChange={(e) => setFormData({ ...formData, schedule_id: e.target.value })}
            disabled={readOnly}
            data-testid="competition-schedule-select"
          >
            <option value="">Select Schedule</option>
            {schedules.map(s => (
              <option key={s.schedule_id} value={s.schedule_id}>{s.schedule_type}</option>
            ))}
          </select>
        </div>
        {showSecondSchedule && (
          <div>
            <div className="flex items-center gap-2 h-6">
              <Label>{secondScheduleLabel}</Label>
              <button
                type="button"
                onClick={() => setHelpOpen(true)}
                className="text-stone-400 hover:text-stone-600 transition-colors"
                title={helpTitle}
                data-testid="second-schedule-help-btn"
              >
                <HelpCircle className="h-4 w-4" />
              </button>
            </div>
            <select
              className="w-full border rounded-lg px-3 py-2 mt-1"
              value={formData.sub_competition_schedule_id}
              onChange={(e) => setFormData({ ...formData, sub_competition_schedule_id: e.target.value })}
              disabled={readOnly}
              data-testid="competition-sub-schedule-select"
            >
              <option value="">Select Schedule</option>
              {getAvailableSubSchedules().map(s => (
                <option key={s.schedule_id} value={s.schedule_id}>{s.schedule_type}</option>
              ))}
            </select>
          </div>
        )}
        <div className="md:col-span-2 flex gap-6 mt-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={formData.figure_events_included}
              onCheckedChange={(val) => setFormData({ ...formData, figure_events_included: val })}
              disabled={readOnly}
            />
            <span>Figure Events Included</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={formData.artistic_events_included}
              onCheckedChange={(val) => setFormData({ ...formData, artistic_events_included: val })}
              disabled={readOnly}
            />
            <span>Artistic Events Included</span>
          </label>
        </div>
      </div>

      <AlertDialog open={helpOpen} onOpenChange={setHelpOpen}>
        <AlertDialogContent data-testid="second-schedule-help-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{helpTitle}</AlertDialogTitle>
            <AlertDialogDescription className="whitespace-pre-line">
              {helpBody}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction data-testid="second-schedule-help-ok">Okay</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
